import React, {useState, useEffect, useReducer} from 'react'
import {
  DASHBOARD,
  KIOSK_CONTAINER,
  MEETING_CREATE,
  MEETING_LIST,
  MEETING_SCHEDULE,
  MEETING_CONFIRMATION,
  CONFERENCE_ROOM
} from '../constants/const'
import Header from '../components/Header/Header'
import HeaderTitle from '../components/Header/HeaderTitle'
import HeaderButton from '../components/Header/HeaderButton'
import Clock from '../components/Clock/Clock'
import BookingList from '../containers/MeetingReservation/BookingList'
import Confirmation from '../components/Confirmation/Confirmation'
import MeetingSchedule from '../containers/MeetingReservation/MeetingSchedule'
import MeetingCreate from '../containers/MeetingReservation/MeetingCreate'
import RoomInfo from '../containers/MeetingReservation/RoomInfo'
import {NavigationButtons} from '../components/Navigation/NavigationButtons/NavigatonButtons'
import styled from 'styled-components'
import {_L, useLocaleState} from '../context/LocaleContext'
import {
  combineDateAndTimeStrings,
  convertTimeNumberToDate,
  getDateFromString
} from '../utils/time'
import {fetchRoomArrange, saveRoomReservation} from '../api/reservations'
import {useUserState} from '../context/UserContext'
import {useViewState} from '../hooks/form-state'
import {generateRecurrenceXml, getSearchTimes} from '../utils/recurrence'
import {useRecurrenceState} from '../context/RecurrenceContext'
import {useUIDispatch, useUIState} from '../context/UIContext'
import {useConfigState, getReservationTimes} from '../context/ConfigContext'
import {useKioskTimer} from '../hooks/kiosk-timer'
import {useViewFetch} from '../utils/fetch'
import {useMeetingState} from '../context/MeetingContext'
import {getReservationStartEndTimes} from '../utils/meeting'

const getInitialState = ({user, room, search, recurrence}) => {
  const {email, employee} = user
  const {phone, id, dvId, dpId, deptCode} = employee
  const {time_start: searchTimeStart, time_end: searchTimeEnd} = search

  const {
    date_start: recurrenceDateStart,
    date_end: recurrenceDateEnd
  } = recurrence

  const {dateRange: dateList, searchTimes: selectedTimes} = getSearchTimes(
    recurrence,
    {isNew: true},
    search
  )

  let meeting = {
    resId: '',
    rmResId: '',
    isNew: true,
    room,
    name: '',
    comments: '',
    attendees: [],
    requestor: {
      em_id: id,
      email,
      phone
    },
    organizer: {
      em_id: id,
      email,
      phone
    },
    department: {
      dvId,
      dpId,
      deptCode
    },
    startDate: recurrenceDateStart,
    endDate: recurrenceDateEnd,
    startTime: searchTimeStart || '',
    endTime: searchTimeEnd || '',
    isPrivate: false,
    verified: false,
    dateList,
    selectedTimes,
    outlookUniqueId: '',
    status: 'Confirmed',
    exceptions: [],
    attendeesInRoom: 0,
    errors: {startTime: null, endTime: null, startDate: null},
    touched: {startTime: null, endTime: null, startDate: null},
    submitError: '',
    isSaving: false
  }

  return {meeting}
}

const updateAttendeesInRoom = ({attendeesInRoom}, attendees) => {
  let numberOfAttendeesInRoom = attendeesInRoom
  if (attendees.length > attendeesInRoom) {
    numberOfAttendeesInRoom = attendees.length
  }

  return numberOfAttendeesInRoom
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'setMeeting':
      return {
        ...state,
        meeting: {
          ...state.meeting,
          ...action.payload
        }
      }

    case 'addAttendee': {
      return {
        ...state,
        meeting: {
          ...state.meeting,
          attendees: [...state.meeting.attendees, action.payload],
          attendeesInRoom: updateAttendeesInRoom(state.meeting, [
            ...state.meeting.attendees,
            action.payload
          ])
        }
      }
    }

    case 'removeAttendee': {
      return {
        ...state,
        meeting: {
          ...state.meeting,
          attendees: state.meeting.attendees.filter(
            item => item.emId !== action.payload
          )
        }
      }
    }

    case 'setSubmitError': {
      return {
        ...state,
        meeting: {
          ...state.meeting,
          submitError: action.payload
        }
      }
    }

    case 'setIsSaving': {
      return {
        ...state,
        meeting: {
          ...state.meeting,
          isSaving: action.payload
        }
      }
    }
  }
}

const validateSchedule = ({meeting, viewState, localeState}) => {
  const {startDate, startTime, endTime, name, department, organizer} = meeting

  if (viewState === MEETING_SCHEDULE) {
    return {
      startDate: startDate
        ? null
        : _L({key: 'Start date is required', localeState}),
      startTime: startTime
        ? null
        : _L({key: 'Start time is required', localeState}),
      endTime: endTime ? null : _L({key: 'End time is required', localeState})
    }
  }

  if (viewState === MEETING_CREATE) {
    return {
      name: name ? null : _L({key: 'Name is required', localeState}),
      department: department.dpId
        ? null
        : _L({key: 'Department is required', localeState}),
      organizer: organizer.em_id
        ? null
        : _L({key: 'Organizer is required', localeState})
    }
  }

  return {}
}

const MeetingSpaceSequence = ({room, parentPage}) => {
  const recurrence = useRecurrenceState()

  const {user} = useUserState()
  const {search} = useMeetingState()
  const [state, dispatch] = useReducer(
    reducer,
    getInitialState({user, room, search, recurrence})
  )
  const uiDispatch = useUIDispatch()
  const localeState = useLocaleState()
  const {kioskId, homeFloor, isKiosk} = useUIState()
  const reservationTimes = getReservationTimes(useConfigState())

  // The meeting state object
  const {meeting} = state
  const isMeetingKiosk = isKiosk && kioskId.eq_std === 'KIOSK-CONFERENCE'

  const {blId, flId, rmId} = room

  const setMeeting = roomArrange => {
    const {configId, rmArrangeTypeId, maxCapacity} = roomArrange
    dispatch({
      type: 'setMeeting',
      payload: {
        room: {blId, flId, rmId, configId, rmArrangeTypeId, maxCapacity}
      }
    })
  }

  const {data: rmArrange} = useViewFetch({
    api: fetchRoomArrange,
    parameters: {blId, flId, rmId},
    initialState: {dayStart: null, dayEnd: null, postBlock: 0},
    onChangeOf: [blId, flId, rmId],
    onCallback: setMeeting
  })

  const submitMeeting = async () => {
    dispatch({type: 'setIsSaving', payload: true})
    meeting.recurrenceXml = generateRecurrenceXml(recurrence)
    try {
      await saveRoomReservation({meeting, recurrence})
      dispatch({type: 'setIsSaving', payload: false})
      return () => MEETING_CONFIRMATION
    } catch (error) {
      console.log(error)
      dispatch({type: 'setSubmitError', payload: error})
    }
    dispatch({type: 'setIsSaving', payload: false})
  }

  const viewStates = {
    MEETING_SCHEDULE: {
      prev: () => {
        uiDispatch({
          type: 'setContent',
          payload: {content: parentPage || DASHBOARD}
        })
      },
      next: () => MEETING_CREATE
    },
    MEETING_CREATE: {
      prev: () => MEETING_SCHEDULE,
      next: () => MEETING_LIST
    },
    MEETING_LIST: {
      prev: () => MEETING_CREATE,
      next: submitMeeting
    },
    MEETING_CONFIRMATION: {
      prev: () => {},
      next: () => DASHBOARD
    }
  }

  const {viewState, dispatchViewState} = useViewState({
    viewStates,
    initialView: MEETING_SCHEDULE
  })

  const useValidate = ({meeting, validateSchedule, viewState, localeState}) => {
    const [errors, setErrors] = useState({
      startDate: null,
      startTime: null,
      endTime: null,
      name: null,
      department: null,
      organizer: null
    })
    const [showError, setShowError] = useState(false)

    const hasErrors = () => {
      let errors = validateSchedule({meeting, viewState, localeState})
      setErrors(errors)
      setShowError(true)
      return Object.keys(errors).some(x => errors[x])
    }

    useEffect(
      () => {
        let errors = validateSchedule({meeting, viewState, localeState})
        setErrors(errors)
      },
      [meeting, viewState, localeState, validateSchedule]
    )

    return {
      errors,
      hasErrors,
      showError,
      setShowError
    }
  }

  const {errors, hasErrors, setShowError, showError} = useValidate({
    meeting,
    validateSchedule,
    viewState,
    localeState
  })

  const timeoutExpired = () => {
    const isConferenceKiosk = kioskId.eq_std === 'KIOSK-CONFERENCE'
    uiDispatch({
      type: 'setContent',
      payload: {
        content: isConferenceKiosk ? 'CONFERENCE_ROOM' : KIOSK_CONTAINER
      }
    })
    uiDispatch({type: 'setActiveFloor', payload: homeFloor})
  }

  const {message, onClickHandler} = useKioskTimer({
    enable: isKiosk,
    inUseDelay: 60000,
    timeoutDelay: 10000,
    timeoutExpirationHandler: timeoutExpired
  })

  const onNext = () => {
    if (!hasErrors()) {
      setShowError(false)
      dispatchViewState({action: 'next'})
    }
  }

  const onPrevious = () => {
    dispatch({type: 'setSubmitError', payload: ''})
    dispatchViewState({action: 'prev'})
  }

  const goToMain = () =>
    uiDispatch({type: 'setContent', payload: {content: parentPage || DASHBOARD}})

  const generateErrorComponent = ({errors, showError}) => {
    if (showError) {
      return Object.values(errors)
        .filter(error => error !== null)
        .map((error, idx) => <div key={idx}>{error}</div>)
    } else {
      return null
    }
  }

  // Event initiated from the Scheduler
  const setMeetingTime = result => {
    const {series, exceptions} = result
    const {start, end, dates} = series

    // Combine dates and times for the selectedTimes
    const startTime = getDateFromString({dateStr: start, format: 'HH:mm'})
    const endTime = getDateFromString({dateStr: end, format: 'HH:mm'})

    let selectedTimes = dates.map(date => {
      let start = combineDateAndTimeStrings(date, startTime)
      let end = combineDateAndTimeStrings(date, endTime)

      return {
        start,
        end
      }
    })

    let meetingTime = {
      startTime: start,
      endTime: end,
      dateList: dates,
      startDate: dates[0],
      endDate: dates[dates.length - 1],
      selectedTimes,
      exceptions,
      touched: {
        startTime: true,
        endTime: true,
        startDate: true
      }
    }
    dispatch({type: 'setMeeting', payload: meetingTime})
  }

  const addMeetingAttendee = attendee => dispatch({type: 'addAttendee', payload: attendee})


  const removeMeetingAttendee = attendee => {
    const {emId} = attendee
    dispatch({type: 'removeAttendee', payload: emId})
  }

  const onHeaderCancel = () => {
    let content = parentPage || DASHBOARD
    if (isKiosk) {
      content = isMeetingKiosk ? CONFERENCE_ROOM : KIOSK_CONTAINER
      uiDispatch({
        type: 'showSearch',
        payload: {show: false, type: null, options: {}}
      })
    }

    uiDispatch({
      type: 'setContent',
      payload: {content}
    })
  }

  const onUpdateMeeting = ({times}) => {
    const dateList = Object.values(times).map(item => {
      return item.date
    })

    const scheduled = Object.values(times).map(item => {
      const {selected, date} = item
      const {start, end} = selected

      return {
        start:
          start === -1
            ? -1
            : convertTimeNumberToDate({date, timeNumber: start}),
        end:
          end === -1
            ? -1
            : convertTimeNumberToDate({date, timeNumber: end + 0.5})
      }
    })

    let payload = {dateList}
    if (scheduled.length && scheduled[0].start !== -1) {
      payload.selectedTimes = scheduled
    }
    dispatch({type: 'setMeeting', payload})
  }

  let workDates = getReservationStartEndTimes({reservationTimes, rmArrange})

  const isMobile = window.matchMedia('(max-width:420px)').matches

  const headerText = isMobile
    ? _L({key: 'Meeting Booking', localeState})
    : _L({key: 'Meeting Space Booking', localeState})

  return (
    <Container onClick={onClickHandler}>
      <Header
        leftContent={<Clock />}
        centerContent={<HeaderTitle title={headerText} />}
        rightContent={
          <HeaderButton
            label={_L({key: 'Cancel', localeState})}
            onClick={onHeaderCancel}
          />
        }
      />
      <StyledMeetingContainer>
        <RoomInfoContainer>
          <RoomInfo room={room} />
        </RoomInfoContainer>
        <SchedulerContainer>
          {
            {
              MEETING_SCHEDULE: (
                <MeetingSchedule
                  meeting={meeting}
                  setMeetingTime={setMeetingTime}
                  dispatch={dispatch}
                  errors={generateErrorComponent({errors, showError})}
                  workDates={workDates}
                  room={room}
                  onUpdateMeeting={onUpdateMeeting}
                />
              ),
              MEETING_CREATE: (
                <MeetingCreate
                  errors={errors}
                  user={user}
                  meeting={meeting}
                  dispatch={dispatch}
                  addMeetingAttendee={addMeetingAttendee}
                  removeMeetingAttendee={removeMeetingAttendee}
                />
              ),
              MEETING_LIST: <BookingList meeting={meeting} />,
              MEETING_CONFIRMATION: (
                <Confirmation
                  title={_L({
                    key: 'Meeting space is successfully booked!',
                    localeState
                  })}
                  subTitle=""
                  goToMain={goToMain}
                />
              )
            }[viewState]
          }
        </SchedulerContainer>
        <NavigationContainer>
          <NavigationButtons
            onNextClick={onNext}
            onBackClick={onPrevious}
            buttonWidth="SMALL"
            loading={meeting.isSaving}
          />
        </NavigationContainer>
      </StyledMeetingContainer>
      {message}
    </Container>
  )
}
export default MeetingSpaceSequence

const Container = styled.div`
  height: 100vh;
  width: 100vw;
`

const StyledMeetingContainer = styled.div`
  display: grid;
  width: 100vw;
  height: calc(100vh - 80px);
  grid-template-rows: 1fr 80px;
  grid-template-columns: 540px calc(100vw - 540px);
  grid-template-areas: 'room schedule' 'room navigation';
  background-color: #eff0f3;
  box-sizing: border-box;

  @media (max-width: 1024px) {
    grid-template-columns: 320px calc(100vw - 320px);
  }

  @media (max-width: 700px) {
    grid-template-rows: auto 1fr 80px;
    grid-template-columns: 100vw;
    grid-template-areas: 'room' 'schedule' 'navigation';
  }
`

const NavigationContainer = styled.div`
  height: 80px;
  display: flex;
  align-items: center;
  padding: 0 10px 0 10px;
  box-sizing: border-box;
  background-color: #eff0f3;
  grid-area: navigation;
`

const RoomInfoContainer = styled.div`
  width: 100%;
  height: 100%;
  box-sizing: border-box;
  grid-area: room;
`

const SchedulerContainer = styled.div`
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  grid-area: schedule;
`
